import { formatPostalCode, type ProviderJob, type ProviderProposal } from '@hemmafixa/shared';
import {
  Button,
  Card,
  DescriptionList,
  DetailSection,
  EmptyState,
  FactGrid,
  InlineConfirm,
  StatusBadge,
  toast,
} from '@hemmafixa/ui';
import { ArrowDown, CalendarClock, Clock, EyeOff, MapPin, SearchX, Send, Tag, Users } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { BackLink } from '../shared/back-link';
import { ContactDetailsList } from '../shared/contact-details';
import { useCategoryLabel } from '../shared/catalogue';
import { JobLocationMap } from '../shared/job-location-map';
import { JobFiles } from '../shared/job-files';
import { JobQuestionList } from '../shared/job-questions';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useEditProposal, useOpenJob, useSendProposal, useWithdrawProposal } from './api';
import { ProposalForm } from './proposal-form';
import { SaveJobButton } from './save-job-button';
import { VerifiedOnly } from './verified-only';

/** An open job as a provider sees it, with their proposal: send, change or withdraw (P4-06). */
export function ProviderJobPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <BackLink to="/provider/jobs" label={t.nav.findJobs} />
      <VerifiedOnly>
        <ProviderJobDetail id={id} />
      </VerifiedOnly>
    </div>
  );
}

/** The job with the provider's proposal form; the full page and Find jobs' details panel both use it. */
export function ProviderJobDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useOpenJob(id);
  return (
    <QueryView query={query} loading="detail">
      {(job) =>
        job ? (
          <JobView job={job} />
        ) : (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        )
      }
    </QueryView>
  );
}

/**
 * The job first, so the provider can judge it: key facts, description, photos and answers. The proposal comes
 * last; the button at the top jumps there and puts the cursor in the first field.
 */
function JobView({ job }: { job: ProviderJob }) {
  const { t, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const proposalRef = useRef<HTMLDivElement>(null);
  const j = t.provider.job;
  const canSend = !job.myProposal && job.status === 'open';

  const goToProposal = () => {
    const section = proposalRef.current;
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    section.querySelector<HTMLElement>('input, textarea')?.focus({ preventScroll: true });
  };

  return (
    <div className="grid gap-4">
      {/* Title, actions and key facts in one card, so the job starts near the top */}
      <Card className="grid gap-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid min-w-0 gap-1.5">
            <h1 className="text-2xl font-bold tracking-tight text-balance text-secondary">{job.title}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={job.status} statuses={statuses.job} />
              {job.myProposal && <StatusBadge status={job.myProposal.status} statuses={statuses.proposal} />}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <SaveJobButton jobId={job.id} saved={job.saved} />
            {(canSend || job.myProposal) && (
              <Button onClick={goToProposal}>
                {canSend ? <Send aria-hidden /> : <ArrowDown aria-hidden />}
                {canSend ? j.sendProposal : j.viewProposal}
              </Button>
            )}
          </div>
        </div>
        <FactGrid
          items={[
            { icon: Tag, label: t.job.category, value: categoryLabel(job.categorySlug) },
            { icon: MapPin, label: t.job.location, value: formatPostalCode(job.postalCode) },
            { icon: CalendarClock, label: t.job.wantedStart, value: t.wantedStart[job.wantedStart] },
            ...(job.publishedAt ? [{ icon: Clock, label: t.job.posted, value: date(job.publishedAt) }] : []),
            { icon: Users, label: j.proposals, value: String(job.proposalCount) },
          ]}
          className="rounded-none border-t border-border bg-transparent px-0 pt-4 pb-0 shadow-none"
        />
      </Card>
      {/* What and where side by side once there is room */}
      <div className="@container">
        <div className="grid gap-4 @2xl:grid-cols-[minmax(0,1fr)_20rem]">
          <DetailSection title={t.job.description}>
            <p className="whitespace-pre-line text-sm leading-relaxed">{job.description}</p>
          </DetailSection>
          <JobLocationMap location={job.location} postalCode={job.postalCode} className="h-full min-h-48" />
        </div>
      </div>
      {job.attachments.length > 0 && (
        <DetailSection title={t.files.title}>
          <JobFiles files={job.attachments} />
        </DetailSection>
      )}
      {job.questions.length > 0 && (
        <DetailSection title={t.job.questions}>
          <JobQuestionList questions={job.questions} />
        </DetailSection>
      )}
      <div ref={proposalRef} className="scroll-mt-20">
        <DetailSection title={j.yourProposal} className="ring-1 ring-primary/40">
          {job.myProposal ? (
            <MyProposal proposal={job.myProposal} />
          ) : job.status === 'open' ? (
            <NewProposal jobId={job.id} />
          ) : (
            <p className="text-sm text-muted-foreground">{j.closedText}</p>
          )}
        </DetailSection>
      </div>
    </div>
  );
}

function NewProposal({ jobId }: { jobId: string }) {
  const send = useSendProposal(jobId);
  return <ProposalForm mutation={send} />;
}

/** The sent proposal; while it is submitted on an open job it can be changed, and withdrawn until it is final. */
function MyProposal({ proposal }: { proposal: ProviderProposal }) {
  const { t, date } = useI18n();
  const statuses = useStatuses();
  const edit = useEditProposal(proposal.id);
  const withdraw = useWithdrawProposal(proposal.id);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const j = t.provider.job;
  const canWithdraw = proposal.status === 'submitted' || proposal.status === 'accepted';

  if (editing) {
    return (
      <ProposalForm
        proposal={proposal}
        mutation={edit}
        onDone={() => setEditing(false)}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={proposal.status} statuses={statuses.proposal} />
        <span className="text-sm text-muted-foreground">
          {fill(j.sentAt, { date: date(proposal.createdAt) })}
        </span>
      </div>
      {proposal.editable && <p className="text-sm text-muted-foreground">{j.waitingText}</p>}
      {proposal.client && <ContactDetailsList contact={proposal.client} />}
      {proposal.conversationId && (
        <Button asChild className="justify-self-start">
          <Link to={`/provider/messages?c=${proposal.conversationId}`}>{t.messages.open}</Link>
        </Button>
      )}
      <ProposalSummary proposal={proposal} />
      {proposal.declineReason && (
        <div className="grid gap-1 rounded-lg border border-border p-4">
          <p className="text-sm font-medium">{j.declineReason}</p>
          <p className="whitespace-pre-line text-sm">{proposal.declineReason}</p>
        </div>
      )}
      {proposal.hasContactDetails && (proposal.status === 'submitted' || proposal.status === 'declined') && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <EyeOff className="mt-0.5 size-4 shrink-0" aria-hidden />
          {j.contactHidden}
        </p>
      )}
      {(proposal.editable || canWithdraw) && !confirming && (
        <div className="flex flex-wrap gap-3">
          {proposal.editable && (
            <Button variant="outline" onClick={() => setEditing(true)}>
              {j.edit}
            </Button>
          )}
          {canWithdraw && (
            <Button variant="outline" className="text-danger" onClick={() => setConfirming(true)}>
              {j.withdraw}
            </Button>
          )}
        </div>
      )}
      <InlineConfirm
        open={confirming}
        onOpenChange={setConfirming}
        title={j.withdrawTitle}
        description={j.withdrawText}
        confirmLabel={j.withdraw}
        cancelLabel={t.common.cancel}
        destructive
        loading={withdraw.isPending}
        onConfirm={() =>
          withdraw.mutate(undefined, {
            onSuccess: () => {
              setConfirming(false);
              toast.success(j.withdrawnToast);
            },
            onError: () => toast.error(j.conflict),
          })
        }
      />
    </div>
  );
}

/** Price, start, duration, what is included and the message, as the provider wrote them */
function ProposalSummary({ proposal }: { proposal: ProviderProposal }) {
  const { t, money, date } = useI18n();
  const j = t.provider.job;
  return (
    <>
      <DescriptionList
        items={[
          {
            label: proposal.priceToOre ? j.priceRange : j.fixedPrice,
            value: (
              <span className="font-semibold tabular-nums">
                {money(proposal.priceFromOre)}
                {proposal.priceToOre ? ` – ${money(proposal.priceToOre)}` : ''}
              </span>
            ),
          },
          ...(proposal.earliestStart ? [{ label: j.start, value: date(proposal.earliestStart) }] : []),
          ...(proposal.durationDays ? [{ label: j.duration, value: String(proposal.durationDays) }] : []),
          ...(proposal.includes ? [{ label: j.includes, value: proposal.includes }] : []),
        ]}
      />
      <p className="whitespace-pre-line text-sm leading-relaxed">{proposal.message}</p>
    </>
  );
}
